import { defineStore } from 'pinia'
import type { AdminGroupReference, AdminMemberReference } from '@/types/admin'
import type {
  RevenueResponseSection,
  RevenueResponseShop,
  RevenueRow,
  RevenueValidationResult,
} from '@/types/revenue'

export interface User {
  msToExpire?: number
  name?: string
  sub?: string
  email?: string
  email_verified?: boolean
  picture?: string
  nickname?: string
}

export interface Shop {
  shopId: number
  shopName: string
  chainId?: number
  chainName?: string
  shoppingCenterId?: number
  shoppingCenterName?: string
  shoppingCenterGroupName?: string
  shoppingCenterGroupId?: number
  sections: Section[]
  creationDate: Date | string
  deactivationDate: Date | string | null
  chainShopId?: string | null
  shoppingCenterShopId?: string
  groupsRelated?: AdminGroupReference[]
  membersRelated?: AdminMemberReference[]
  canEditShop?: boolean
  isDisabled?: boolean
  notificationGroups?: number[]
}

export interface RevenueIndication {
  data: Array<{
    shopId: number
    sectionIds: number[]
  }>
  length: number
}

export interface RevenueInputResponse {
  revenueDate?: Date | string
  revenueType?: number
  revenues?: RevenueRow[]
  revenueSections?: RevenueResponseSection[]
  revenueShops?: RevenueResponseShop[]
  validation?: RevenueValidationResult
}

export interface Chain {
  chainId: number
  chainName: string
  chainExternalId?: string
  chainGroupId?: number
  chainGroupName?: string
  shopsRelated?: { shopId: number }[]
  groupsRelated?: AdminGroupReference[]
}

export interface Section extends RevenueResponseSection {
  sectionExternalId?: string
  shoppingCenterGroupId?: number
  shoppingCenterGroupName?: string
}

export interface NotificationGroup {
  groupId: number
  groupName: string
  groupDescription: string
  canUpdateNotificationGroup?: boolean
  shopIds: number[]
  groupType?: string
  members?: Member[]
}

export interface Member {
  memberId?: number
  memberFirstName?: string
  memberEmail?: string
  memberPhoneNumber?: number
  memberPositionName?: string
  memberComment?: string
  groupName?: string
  groupId?: number
  shopsRelated?: number[]
  notificationStrategy?: number
  notificationStrategyName?: string
  canUpdateMember?: boolean
  email?: string
  phoneNumber?: number
  allowPhone?: boolean
  allowSms?: boolean
  allowWhatsapp?: boolean
  allowEmail?: boolean
  positionName?: string
  comment?: string
}

export interface Strategy {
  name: string
  id: number
}

export interface ShoppingCenter {
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  shoppingCenterGroupId?: number
}

export interface AppState {
  user: User | null
  allowedShopsList: Shop[] | null
  revenueIndications: RevenueIndication | null
  revenueInputResponse: RevenueInputResponse | null
  chainsList: Chain[] | null
  sectionsList: Section[] | null
  notificationGroups: NotificationGroup[] | null
  membersList: Member[] | null
  strategies: Strategy[] | null
  shoppingCenters: ShoppingCenter[] | null
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    user: null,
    allowedShopsList: null,
    revenueIndications: null,
    revenueInputResponse: null,
    chainsList: null,
    sectionsList: null,
    notificationGroups: null,
    membersList: null,
    strategies: null,
    shoppingCenters: null,
  }),

  getters: {
    getAuthenticatedUser: (state) => state.user,
    
    getAllowedShopsList: (state) => {
      if (state.allowedShopsList) {
        return state.allowedShopsList.map((shop) => ({
          ...shop,
          creationDate: new Date(`${shop.creationDate}Z`),
          deactivationDate: shop.deactivationDate === null 
            ? shop.deactivationDate 
            : new Date(`${shop.deactivationDate}Z`),
        }))
      }
      return state.allowedShopsList
    },

    getMemberList: (state) => state.membersList,
    getChains: (state) => state.chainsList,
    getSections: (state) => state.sectionsList,
    getRevenueIndication: (state) => state.revenueIndications,
    getRevenueInputResponse: (state) => state.revenueInputResponse,
    getNotificationGroups: (state) => state.notificationGroups,
    getMembers: (state) => state.membersList,
    getUserMsToExpire: (state) => state.user?.msToExpire,
    getStrategies: (state) => state.strategies,
    getShoppingCenters: (state) => state.shoppingCenters,
  },

  actions: {
    setAuthenticatedUser(user: User) {
      this.user = user
    },

    setAllowedShopsList(shopsList: Shop[]) {
      this.allowedShopsList = shopsList
    },

    setRevenueIndication(revenueIndication: RevenueIndication) {
      this.revenueIndications = revenueIndication
    },

    setRevenueInputResponse(revenueInputResponse: RevenueInputResponse) {
      this.revenueInputResponse = revenueInputResponse
    },

    setChains(chainsList: Chain[]) {
      this.chainsList = chainsList
    },

    setSections(sectionsList: Section[]) {
      this.sectionsList = sectionsList
    },

    setNotificationGroups(notificationGroups: NotificationGroup[]) {
      this.notificationGroups = notificationGroups
    },

    setMembersList(membersList: Member[]) {
      this.membersList = membersList
    },

    resetState() {
      this.user = null
      this.allowedShopsList = null
      this.revenueIndications = null
      this.revenueInputResponse = null
      this.chainsList = null
      this.sectionsList = null
      this.notificationGroups = null
      this.membersList = null
      this.strategies = null
      this.shoppingCenters = null
    },

    setUserMsToExpire(msToExpire: number) {
      if (this.user) {
        this.user.msToExpire = msToExpire
      } else {
        this.user = { msToExpire }
      }
    },

    setStrategies(strategies: Strategy[]) {
      this.strategies = strategies
    },

    setShoppingCenters(shoppingCenters: ShoppingCenter[]) {
      this.shoppingCenters = shoppingCenters
    },

    addOrUpdateAllowedShopsList(updatedShop: Shop) {
      if (!this.allowedShopsList) {
        this.allowedShopsList = []
      }

      const index = this.allowedShopsList.findIndex(shop => shop.shopId === updatedShop.shopId)
      if (index >= 0) {
        Object.assign(this.allowedShopsList[index], updatedShop)
      } else {
        this.allowedShopsList.push(updatedShop)
      }
    },

    createGroup(newGroup: NotificationGroup) {
      if (!this.notificationGroups) {
        this.notificationGroups = []
      }

      const index = this.notificationGroups.findIndex(group => group.groupId === newGroup.groupId)
      if (index === -1) {
        this.notificationGroups = [...this.notificationGroups, newGroup]
      }
    },

    updateGroup(updatedGroup: Partial<NotificationGroup>) {
      if (this.notificationGroups) {
        // Use loose equality to handle string/number mismatches
        const index = this.notificationGroups.findIndex(g => g.groupId == updatedGroup.groupId)
        if (index !== -1) {
          Object.assign(this.notificationGroups[index], updatedGroup)
        }
      }
    },

    extendChainsList(newChain: Chain) {
      if (!this.chainsList) {
        this.chainsList = []
      }

      const index = this.chainsList.findIndex(chain => chain.chainId === newChain.chainId)
      if (index === -1) {
        this.chainsList.push(newChain)
      }
    },

    updateChainInList(updatedChain: Chain) {
      if (this.chainsList) {
        const index = this.chainsList.findIndex(chain => chain.chainId === updatedChain.chainId)
        if (index !== -1) {
          const newChain = { ...this.chainsList[index], ...updatedChain }
          this.chainsList = [
            ...this.chainsList.slice(0, index),
            newChain,
            ...this.chainsList.slice(index + 1)
          ]
        }
      }
    },

    extendSectionsList(newSection: Section) {
      if (!this.sectionsList) {
        this.sectionsList = []
      }

      const index = this.sectionsList.findIndex(section => section.sectionId === newSection.sectionId)
      if (index === -1) {
        this.sectionsList.push(newSection)
      }
    },

    updateSectionInList(updatedSection: Section) {
      if (this.sectionsList) {
        // Use loose equality to handle potential string/number mismatches
        const index = this.sectionsList.findIndex(section => section.sectionId == updatedSection.sectionId)
        if (index !== -1) {
          // Create a new array reference to ensure reactivity triggers for all subscribers
          const newSection = { ...this.sectionsList[index], ...updatedSection }
          this.sectionsList = [
            ...this.sectionsList.slice(0, index),
            newSection,
            ...this.sectionsList.slice(index + 1)
          ]
        }
      }
    },
  },
})
